import { Title, Text, Group, Stack, Button, Collapse, Container } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { StatusOper } from '../../../libraries/zps';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import ModalComponent from '../modal';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

interface StatusOperModalProps {
  opened: boolean;
  onClose: () => void;
  statusOper: StatusOper | null;
  successMessage?: string; // custom success message
  errorMessage?: string; // custom error message
}

enum MODE {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  DEFAULT = 'default'
}

const MODE_CONFIG = {
  [MODE.SUCCESS]: {
    icon: Icon.IconCircleCheckFilled,
    className: 'status-ok',
    title: 'Success'
  },
  [MODE.ERROR]: {
    icon: Icon.IconXboxXFilled,
    className: 'status-not-ok',
    title: 'Error'
  },
  [MODE.WARNING]: {
    icon: Icon.IconAlertCircleFilled,
    className: 'alert-icon',
    title: 'Warning'
  },
  [MODE.DEFAULT]: {
    icon: Icon.IconAlertCircleFilled,
    className: 'alert-icon',
    title: 'Status'
  }
};

const getMode = (statusOper?: StatusOper | null): MODE => {
  if (!statusOper) return MODE.DEFAULT;
  if (statusOper.code === 0) return MODE.SUCCESS;
  if (statusOper.code > 0) return MODE.ERROR;
  return MODE.DEFAULT;
};

const StatusOperModal = ({ opened, onClose, statusOper, successMessage, errorMessage }: StatusOperModalProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const [detailsOpened, { toggle, close }] = useDisclosure(false); // get `close` method

  const mode = useMemo(() => getMode(statusOper), [statusOper]);
  const config = MODE_CONFIG[mode];
  const IconComponent = config.icon;

  const displayMessage = useMemo(() => {
    if (!statusOper) return 'No message available';
    if (mode === MODE.SUCCESS && successMessage) return successMessage;
    if (mode === MODE.ERROR && errorMessage) return errorMessage;
    return statusOper.message ?? 'No message available';
  }, [statusOper, successMessage, errorMessage, mode]);

  useEffect(() => {
    if (!opened) {
      close();
    }
  }, [opened, close]);

  return (
    <ModalComponent
      show={opened}
      handleClick={onClose}
      classname="status-oper-modal"
      content={
        <Container className="container-modal">
          {/* Header */}
          <Group className="icon-title" align="center" gap="sm">
            <IconComponent className={`${config.className} status-icon`} size="2.5em" />
            <Title order={2}>{displayMessage}</Title>
          </Group>

          {/* Details toggle */}
          {(mode === MODE.ERROR || mode === MODE.WARNING) && (
            <Group onClick={toggle} className="collapsed-button">
              <Text>{lang.GLOBAL_ERROR_DETAILS}</Text>
              {detailsOpened ? <Icon.IconChevronUp size={18} /> : <Icon.IconChevronDown size={18} />}
            </Group>
          )}

          {/* Details content */}
          {(mode === MODE.ERROR || mode === MODE.WARNING) && (
            <Collapse in={detailsOpened}>
              <Stack className="error-details">
                <Group className="code-details">
                  <Title order={6}>{`${lang.ALERT_ERROR_CODE}:`}</Title>
                  <Text>{statusOper?.code ?? '-'}</Text>
                </Group>
                <Group className="message-details">
                  <Title order={6}>{`${lang.ALERT_ERROR_MESSAGE}:`}</Title>
                  <Text>{statusOper?.message ?? '-'}</Text>
                </Group>
              </Stack>
            </Collapse>
          )}

          {/* Footer */}
          {(mode === MODE.ERROR || mode === MODE.WARNING) && (
            <Group className="button-container" justify="center">
              <Button radius="md" onClick={onClose}>
                OK
              </Button>
            </Group>
          )}
        </Container>
      }
    />
  );
};

export default StatusOperModal;
